import { computed, DestroyRef, inject } from '@angular/core';
import {
  updateState,
  withDevtools,
  withDevToolsStub,
  withGlitchTracking,
} from '@angular-architects/ngrx-toolkit';
import {
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';

import { ConfigService } from '../services/config/config.service';
import { AccountingTransactionService } from '../services/accounting/accounting-transaction.service';
import { AccountingReportsService } from '../services/accounting/accounting-reports.service';
import { FarmSwitchService } from '../services/farm-switch/farm-switch.service';
import { FarmStore } from './farm.store';
import { NotificationStore } from './notification.store';
import { LoggerService } from '../services/logger/logger.service';
import {
  SuccessNotification,
  ErrorNotification,
} from '../models/notifications/app-notification';
import {
  CreateIncomeRequest,
  CreateExpenseRequest,
  CreatePersonalIncomeRequest,
  CreatePersonalExpenseRequest,
  CreateTransferRequest,
  CreateBuyAssetWithLoanRequest,
  CreateLoanPaymentRequest,
  CreateBuyPrepaidInputsRequest,
  CreateApplyPrepaidInputsRequest,
  CreateAdjustingEntryRequest,
  SetOpeningBalancesRequest,
  CloseYearRequest,
} from '../entities/accounting/request.entities';
import {
  LedgerAccountPatch,
  LedgerAccount,
} from '../entities/accounting/account.entities';
import { LoadingMap } from './generic-entity.store';
import { withCondition } from './utils/with-condition';
import { LedgerStatistics } from '../entities/accounting/ledgerStatistics.entities';
import { LedgerLineItem } from '../entities/accounting/entry.entities';
import {
  BalanceSheet,
  IncomeStatement,
  TrialBalance,
  AccountBalance,
  AgingReport,
  ScheduleFReport,
} from '../entities/accounting/report.entities';

interface AccountingState {
  accounts: LedgerAccount[];
  selectedAccount: LedgerAccount | null;
  accountActivity: LedgerLineItem[];
  balanceSheet: BalanceSheet | null;
  incomeStatement: IncomeStatement | null;
  trialBalance: TrialBalance | null;
  accountBalance: AccountBalance | null;
  agingReport: AgingReport | null;
  scheduleF: ScheduleFReport | null;
  statistics: LedgerStatistics | null;
  loading: boolean;
  loadingMap: LoadingMap;
  error: string | null;
  isBootstrapped: boolean;
}

const initialState: AccountingState = {
  accounts: [],
  selectedAccount: null,
  accountActivity: [],
  balanceSheet: null,
  incomeStatement: null,
  trialBalance: null,
  accountBalance: null,
  agingReport: null,
  scheduleF: null,
  statistics: null,
  loading: false,
  loadingMap: {
    loading: false,
    loaded: false,
    error: null,
    pagination: {
      skip: 0,
      limit: 100,
      total: 0,
      loadedCount: 0,
      currentPage: 0,
      totalPages: 0,
      hasMore: false,
      loadedPages: new Set<number>(),
    },
    statistics: {
      loading: false,
      loaded: false,
      error: null,
    },
    search: {
      loading: false,
      error: null,
    },
  },
  error: null,
  isBootstrapped: false,
};

export const AccountingStore = signalStore(
  withState(initialState),
  withProps(() => ({
    configService: inject(ConfigService),
    transactionService: inject(AccountingTransactionService),
    reportsService: inject(AccountingReportsService),
    farmStore: inject(FarmStore),
    notificationStore: inject(NotificationStore),
    logger: inject(LoggerService),
  })),
  withComputed(
    ({
      accounts,
      selectedAccount,
      loading,
      loadingMap,
      statistics,
      balanceSheet,
      incomeStatement,
      trialBalance,
      scheduleF,
      isBootstrapped,
    }) => {
      const logger = inject(LoggerService);
      return {
        hasAccounts: computed(() => {
          const result = accounts().length > 0;
          logger.log(`AccountingStore computed hasAccounts: ${result}`);
          return result;
        }),
        accountsCount: computed(() => {
          const count = accounts().length;
          logger.log(`AccountingStore computed accountsCount: ${count}`);
          return count;
        }),
        isLoading: computed(() => {
          const result = loading() || loadingMap().loading;
          logger.log(`AccountingStore computed isLoading: ${result}`);
          return result;
        }),
        isStatisticsLoading: computed(() => {
          const result = loadingMap().statistics.loading;
          logger.log(`AccountingStore computed isStatisticsLoading: ${result}`);
          return result;
        }),
        hasError: computed(() => {
          const result = loadingMap().error !== null;
          logger.log(`AccountingStore computed hasError: ${result}`);
          return result;
        }),
        errorMessage: computed(() => loadingMap().error),
        selectedAccountName: computed(() => selectedAccount()?.name || null),
        hasStatistics: computed(() => statistics() !== null),
        hasBalanceSheet: computed(() => balanceSheet() !== null),
        hasIncomeStatement: computed(() => incomeStatement() !== null),
        hasTrialBalance: computed(() => trialBalance() !== null),
        hasScheduleF: computed(() => scheduleF() !== null),
        accountingIsBootstrapped: computed(() => {
          const result = isBootstrapped();
          logger.log(`AccountingStore computed accountingIsBootstrapped: ${result}`);
          return result;
        }),
      };
    },
  ),
  withCondition(
    ({ configService }) => !configService?.environment?.production,
    withDevtools('accounting', withGlitchTracking()),
    withDevToolsStub('accounting'),
  ),
  withMethods((store) => ({
    postIncome(data: CreateIncomeRequest): void {
      updateState(store, '[Accounting] Post Income Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.postIncome(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Post Income Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Income recorded successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to post income'),
      });
    },

    recordExpense(data: CreateExpenseRequest): void {
      updateState(store, '[Accounting] Record Expense Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.recordExpense(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Record Expense Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Expense recorded successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to record expense'),
      });
    },

    recordPersonalIncome(data: CreatePersonalIncomeRequest): void {
      updateState(store, '[Accounting] Record Personal Income Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.recordPersonalIncome(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Record Personal Income Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Personal income recorded successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to record personal income'),
      });
    },

    recordPersonalExpense(data: CreatePersonalExpenseRequest): void {
      updateState(store, '[Accounting] Record Personal Expense Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.recordPersonalExpense(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Record Personal Expense Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Personal expense recorded successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(
            error.message || 'Failed to record personal expense',
          ),
      });
    },

    transfer(data: CreateTransferRequest): void {
      updateState(store, '[Accounting] Transfer Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.transfer(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Transfer Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Transfer completed successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to transfer'),
      });
    },

    buyAssetWithLoan(data: CreateBuyAssetWithLoanRequest): void {
      updateState(store, '[Accounting] Buy Asset With Loan Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.buyAssetWithLoan(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Buy Asset With Loan Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Asset purchase with loan recorded successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to buy asset with loan'),
      });
    },

    loanPayment(data: CreateLoanPaymentRequest): void {
      updateState(store, '[Accounting] Loan Payment Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.loanPayment(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Loan Payment Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Loan payment recorded successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to process loan payment'),
      });
    },

    buyPrepaidInputs(data: CreateBuyPrepaidInputsRequest): void {
      updateState(store, '[Accounting] Buy Prepaid Inputs Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.buyPrepaidInputs(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Buy Prepaid Inputs Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Prepaid inputs purchased successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to buy prepaid inputs'),
      });
    },

    applyPrepaidInputs(data: CreateApplyPrepaidInputsRequest): void {
      updateState(store, '[Accounting] Apply Prepaid Inputs Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.applyPrepaidInputs(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Apply Prepaid Inputs Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Prepaid inputs applied successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to apply prepaid inputs'),
      });
    },

    setOpeningBalances(data: SetOpeningBalancesRequest): void {
      updateState(store, '[Accounting] Set Opening Balances Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.setOpeningBalances(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Set Opening Balances Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Opening balances set successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to set opening balances'),
      });
    },

    createAdjustingEntry(data: CreateAdjustingEntryRequest): void {
      updateState(store, '[Accounting] Create Adjusting Entry Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.createAdjustingEntry(farmId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Create Adjusting Entry Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Adjusting entry created successfully',
            }),
          );
          this.loadTransactions();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to create adjusting entry'),
      });
    },

    loadAccounts(q?: string): void {
      store.logger.log('AccountingStore loadAccounts called');
      updateState(store, '[Accounting] Load Accounts Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.searchAccounts(farmId, q).subscribe({
        next: (accounts) => {
          store.logger.log(`AccountingStore loadAccounts success, count: ${accounts.length}`);
          updateState(store, '[Accounting] Load Accounts Success', {
            accounts,
            loadingMap: {
              ...store.loadingMap(),
              loading: false,
              loaded: true,
              error: null,
            },
          });
        },
        error: (error) =>
          this.handleLoadingMapError(
            error.message || 'Failed to load accounts',
          ),
      });
    },

    loadAccountById(accountId: string): void {
      updateState(store, '[Accounting] Load Account By ID Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.getAccountById(farmId, accountId).subscribe({
        next: (account) => {
          updateState(store, '[Accounting] Load Account By ID Success', {
            selectedAccount: account,
            loadingMap: { ...store.loadingMap(), loading: false, error: null },
          });
        },
        error: (error) =>
          this.handleLoadingMapError(error.message || 'Failed to load account'),
      });
    },

    patchAccount(accountId: string, data: LedgerAccountPatch): void {
      updateState(store, '[Accounting] Patch Account Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.patchAccount(farmId, accountId, data).subscribe({
        next: () => {
          updateState(store, '[Accounting] Patch Account Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Account updated successfully',
            }),
          );
          this.loadAccounts();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to update account'),
      });
    },

    loadAccountActivity(
      accountId: string,
      startDate?: Date,
      endDate?: Date,
    ): void {
      store.logger.log(`AccountingStore loadAccountActivity called with accountId: ${accountId}`);
      updateState(store, '[Accounting] Load Account Activity Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService
        .getAccountActivity(farmId, accountId, startDate, endDate)
        .subscribe({
          next: (activity) => {
            store.logger.log(`AccountingStore loadAccountActivity success, count: ${activity.length}`);
            updateState(store, '[Accounting] Load Account Activity Success', {
              accountActivity: activity,
              loadingMap: {
                ...store.loadingMap(),
                loading: false,
                error: null,
              },
            });
          },
          error: (error) =>
            this.handleLoadingMapError(
              error.message || 'Failed to load account activity',
            ),
        });
    },

    loadTransactions(startDate?: Date, endDate?: Date): void {
      store.logger.log('AccountingStore loadTransactions called');
      updateState(store, '[Accounting] Load Transactions Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleLoadingMapError('Farm ID is required');
        return;
      }

      const loadAccountsAndActivity$ = store.loadingMap().loaded
        ? of(store.accounts())
        : store.transactionService.searchAccounts(farmId);

      loadAccountsAndActivity$
        .pipe(
          switchMap((accounts) => {
            store.logger.log(`AccountingStore loadTransactions: Loading activity for ${accounts.length} accounts`);
            if (!store.loadingMap().loaded) {
              updateState(store, '[Accounting] Load Transactions - Accounts Loaded', {
                accounts,
                loadingMap: {
                  ...store.loadingMap(),
                  loaded: true,
                },
              });
            }

            if (accounts.length === 0) {
              return of([]);
            }

            const activityRequests = accounts.map((account) =>
              store.transactionService
                .getAccountActivity(farmId, account.id, startDate, endDate)
                .pipe(
                  catchError((error) => {
                    store.logger.log(`AccountingStore loadTransactions: Error loading activity for account ${account.id}: ${error.message}`);
                    return of([]);
                  })
                )
            );

            return forkJoin(activityRequests);
          }),
          map((activityArrays) => {
            const allActivity = activityArrays.flat();
            store.logger.log(`AccountingStore loadTransactions: Combined ${allActivity.length} total transactions`);
            return allActivity;
          }),
          catchError((error) => {
            this.handleLoadingMapError(
              error.message || 'Failed to load transactions'
            );
            return of([]);
          })
        )
        .subscribe({
          next: (allActivity) => {
            store.logger.log(`AccountingStore loadTransactions success, total count: ${allActivity.length}`);
            updateState(store, '[Accounting] Load Transactions Success', {
              accountActivity: allActivity,
              loadingMap: {
                ...store.loadingMap(),
                loading: false,
                error: null,
              },
            });
          },
          error: (error) =>
            this.handleLoadingMapError(
              error.message || 'Failed to load transactions'
            ),
        });
    },

    bootstrapAccounting(): void {
      updateState(store, '[Accounting] Bootstrap Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.bootstrapAccounting(farmId).subscribe({
        next: () => {
          updateState(store, '[Accounting] Bootstrap Success', {
            loading: false,
            error: null,
            isBootstrapped: true,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Accounting bootstrapped successfully',
            }),
          );
          this.loadAccounts();
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to bootstrap accounting'),
      });
    },

    closeYearEnd(year: number, memo?: string, occurredAt?: string): void {
      updateState(store, '[Accounting] Close Year End Start', {
        loading: true,
        error: null,
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      const request: CloseYearRequest = {
        year,
        memo,
        occurred_at: occurredAt,
      };
      store.transactionService.closeYearEnd(farmId, request).subscribe({
        next: () => {
          updateState(store, '[Accounting] Close Year End Success', {
            loading: false,
            error: null,
          });
          store.notificationStore.addNotification(
            new SuccessNotification({
              summary: 'Success',
              detail: 'Year end closed successfully',
            }),
          );
        },
        error: (error) =>
          this.handleError(error.message || 'Failed to close year end'),
      });
    },

    loadBalanceSheet(asOf?: string): void {
      updateState(store, '[Accounting] Load Balance Sheet Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.reportsService.getBalanceSheet(farmId, asOf ? new Date(asOf) : undefined).subscribe({
        next: (balanceSheet) => {
          updateState(store, '[Accounting] Load Balance Sheet Success', {
            balanceSheet,
            loadingMap: { ...store.loadingMap(), loading: false, error: null },
          });
        },
        error: (error) =>
          this.handleLoadingMapError(
            error.message || 'Failed to load balance sheet',
          ),
      });
    },

    loadIncomeStatement(year: number): void {
      updateState(store, '[Accounting] Load Income Statement Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.reportsService.getIncomeStatement(farmId, year).subscribe({
        next: (incomeStatement) => {
          updateState(store, '[Accounting] Load Income Statement Success', {
            incomeStatement,
            loadingMap: { ...store.loadingMap(), loading: false, error: null },
          });
        },
        error: (error) =>
          this.handleLoadingMapError(
            error.message || 'Failed to load income statement',
          ),
      });
    },

    loadTrialBalance(asOf?: string): void {
      updateState(store, '[Accounting] Load Trial Balance Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.reportsService.getTrialBalance(farmId, asOf ? new Date(asOf) : undefined).subscribe({
        next: (trialBalance) => {
          updateState(store, '[Accounting] Load Trial Balance Success', {
            trialBalance,
            loadingMap: { ...store.loadingMap(), loading: false, error: null },
          });
        },
        error: (error) =>
          this.handleLoadingMapError(
            error.message || 'Failed to load trial balance',
          ),
      });
    },

    loadAccountBalance(
      accountCodeOrName: string,
      start?: string,
      end?: string,
    ): void {
      updateState(store, '[Accounting] Load Account Balance Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.reportsService
        .getAccountBalance(farmId, accountCodeOrName, start ? new Date(start) : undefined, end ? new Date(end) : undefined)
        .subscribe({
          next: (accountBalance) => {
            updateState(store, '[Accounting] Load Account Balance Success', {
              accountBalance,
              loadingMap: {
                ...store.loadingMap(),
                loading: false,
                error: null,
              },
            });
          },
          error: (error) =>
            this.handleLoadingMapError(
              error.message || 'Failed to load account balance',
            ),
        });
    },

    loadAgingReport(
      asOf?: string,
      reportType?: 'receivable' | 'payable',
    ): void {
      updateState(store, '[Accounting] Load Aging Report Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.reportsService
        .getAgingReport(farmId, asOf ? new Date(asOf) : undefined, reportType ? [reportType] : undefined)
        .subscribe({
          next: (agingReport) => {
            updateState(store, '[Accounting] Load Aging Report Success', {
              agingReport,
              loadingMap: {
                ...store.loadingMap(),
                loading: false,
                error: null,
              },
            });
          },
          error: (error) =>
            this.handleLoadingMapError(
              error.message || 'Failed to load aging report',
            ),
        });
    },

    loadScheduleF(year: number): void {
      updateState(store, '[Accounting] Load Schedule F Start', {
        loadingMap: { ...store.loadingMap(), loading: true, error: null },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.reportsService.getScheduleF(farmId, year).subscribe({
        next: (scheduleF) => {
          updateState(store, '[Accounting] Load Schedule F Success', {
            scheduleF,
            loadingMap: { ...store.loadingMap(), loading: false, error: null },
          });
        },
        error: (error) =>
          this.handleLoadingMapError(
            error.message || 'Failed to load Schedule F',
          ),
      });
    },

    loadStatistics(): void {
      updateState(store, '[Accounting] Load Statistics Start', {
        loadingMap: {
          ...store.loadingMap(),
          statistics: { loading: true, loaded: false, error: null },
        },
      });
      const farmId = store.farmStore.currentFarm()?.id;
      if (!farmId) {
        this.handleError('Farm ID is required');
        return;
      }
      store.transactionService.getTransactionStatistics(farmId).subscribe({
        next: (statistics) => {
          updateState(store, '[Accounting] Load Statistics Success', {
            statistics,
            loadingMap: {
              ...store.loadingMap(),
              statistics: { loading: false, loaded: true, error: null },
            },
          });
        },
        error: (error) => {
          updateState(store, '[Accounting] Load Statistics Error', {
            loadingMap: {
              ...store.loadingMap(),
              statistics: {
                loading: false,
                loaded: false,
                error: error.message || 'Failed to load statistics',
              },
            },
          });
        },
      });
    },

    selectAccount(account: LedgerAccount | null): void {
      updateState(store, '[Accounting] Select Account', {
        selectedAccount: account,
      });
    },

    selectAccountById(accountId: string): void {
      const account = store.accounts().find((a) => a.id === accountId) || null;
      this.selectAccount(account);
    },

    clearError(): void {
      updateState(store, '[Accounting] Clear Error', {
        error: null,
        loadingMap: { ...store.loadingMap(), error: null },
      });
    },

    setLoading(loading: boolean): void {
      updateState(store, '[Accounting] Set Loading', { loading });
    },

    reset(): void {
      store.logger.log('AccountingStore reset called');
      updateState(store, '[Accounting] Reset', initialState);
    },

    initialize(): void {
      store.logger.log('AccountingStore initialize called');
      this.loadAccounts();
      this.loadStatistics();
    },

    handleError(message: string): void {
      updateState(store, '[Accounting] Error', {
        loading: false,
        error: message,
      });
      store.notificationStore.addNotification(
        new ErrorNotification({ summary: 'Error', detail: message }),
      );
    },

    handleLoadingMapError(message: string): void {
      updateState(store, '[Accounting] Loading Error', {
        loadingMap: { ...store.loadingMap(), loading: false, error: message },
      });
      store.notificationStore.addNotification(
        new ErrorNotification({ summary: 'Error', detail: message }),
      );
    },

    shouldLoadAccounts(): boolean {
      const map = store.loadingMap();
      return !map.loading && !map.loaded;
    },

    shouldLoadList(): boolean {
      const map = store.loadingMap();
      return !map.loading && !map.loaded;
    },

    shouldLoadTransactions(): boolean {
      const map = store.loadingMap();
      const hasTransactions = store.accountActivity().length > 0;
      return !map.loading && (!map.loaded || !hasTransactions);
    },

    getAccountById(id: string): LedgerAccount | undefined {
      return store.accounts().find((account) => account.id === id);
    },

    hasAccessToAccount(accountId: string): boolean {
      return store.accounts().some((account) => account.id === accountId);
    },
  })),
  withHooks({
    onInit(store) {
      store.logger.log('AccountingStore onInit hook triggered');
      const destroyRef = inject(DestroyRef);
      const farmSwitchService = inject(FarmSwitchService);

      farmSwitchService.onFarmSwitch$
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(() => {
          store.reset();
        });
    },
  }),
);
